import React, { useEffect, useRef, useState } from 'react';
import './EvolutionAnimation.css';

/**
 * Evolution Animation Component
 * Displays a beautiful evolution animation when Pokemon evolves
 * Shows both Pokemon images overlapping and switching
 */
const EvolutionAnimation = ({ 
    oldPokemonImage, 
    newPokemonImage, 
    onComplete,
    show = false 
}) => {
    const oldImageRef = useRef(null);
    const newImageRef = useRef(null);
    const containerRef = useRef(null);
    const [currentStep, setCurrentStep] = useState(0);
    
    useEffect(() => {
        if (!show || !oldPokemonImage || !newPokemonImage) return;
        
        let animationTimeout;
        let blinkTimeout;
        let blinkCount = 0;
        const maxBlinks = 8;
        
        // Step 1: White flash (500ms)
        const flashStep = () => {
            if (oldImageRef.current) {
                oldImageRef.current.style.filter = 'brightness(10)';
                oldImageRef.current.style.opacity = '1';
            }
            setCurrentStep(1);
            
            animationTimeout = setTimeout(() => {
                if (oldImageRef.current) {
                    oldImageRef.current.style.filter = 'brightness(1)';
                }
                blinkStep();
            }, 500);
        };
        
        // Step 2: Blinking between old and new (1500ms)
        const blinkStep = () => {
            setCurrentStep(2);
            blinkCount = 0;
            
            const blink = () => {
                if (blinkCount >= maxBlinks * 2) {
                    fadeInStep();
                    return;
                }
                
                if (blinkCount % 2 === 0) {
                    // Show old Pokemon
                    if (oldImageRef.current) {
                        oldImageRef.current.style.opacity = '1';
                        oldImageRef.current.style.zIndex = '2';
                    }
                    if (newImageRef.current) {
                        newImageRef.current.style.opacity = '0';
                        newImageRef.current.style.zIndex = '1';
                    }
                } else {
                    // Show new Pokemon
                    if (oldImageRef.current) {
                        oldImageRef.current.style.opacity = '0';
                        oldImageRef.current.style.zIndex = '1';
                    }
                    if (newImageRef.current) {
                        newImageRef.current.style.opacity = '1';
                        newImageRef.current.style.zIndex = '2';
                    }
                }
                
                blinkCount++;
                blinkTimeout = setTimeout(blink, 1500 / (maxBlinks * 2));
            };
            
            blink();
        };
        
        // Step 3: Fade in evolved Pokemon (1000ms)
        const fadeInStep = () => {
            setCurrentStep(3);
            if (oldImageRef.current) {
                oldImageRef.current.style.opacity = '0';
                oldImageRef.current.style.transition = 'opacity 0.5s ease-out';
            }
            if (newImageRef.current) {
                newImageRef.current.style.opacity = '1';
                newImageRef.current.style.transition = 'opacity 1s ease-in';
                newImageRef.current.style.zIndex = '2';
            }
            
            animationTimeout = setTimeout(() => {
                if (onComplete) {
                    onComplete();
                }
            }, 1000);
        };
        
        // Reset and start
        if (oldImageRef.current) {
            oldImageRef.current.style.opacity = '1';
            oldImageRef.current.style.filter = '';
            oldImageRef.current.style.transition = '';
            oldImageRef.current.style.zIndex = '2';
        }
        if (newImageRef.current) {
            newImageRef.current.style.opacity = '0';
            newImageRef.current.style.transition = '';
            newImageRef.current.style.zIndex = '1';
        }
        
        flashStep();
        
        return () => {
            if (animationTimeout) clearTimeout(animationTimeout);
            if (blinkTimeout) clearTimeout(blinkTimeout);
        };
    }, [show, oldPokemonImage, newPokemonImage, onComplete]);
    
    if (!show) return null;
    
    return (
        <div 
            ref={containerRef}
            className="evolution-animation-container"
        >
            <div className="evolution-animation-overlay">
                <div className="evolution-images-wrapper">
                    <img
                        ref={oldImageRef}
                        className="evolution-pokemon-image evolution-old"
                        src={oldPokemonImage}
                        alt="Old Pokemon"
                        onError={(e) => {
                            e.target.src = '/images/pokemon/1.gif';
                        }}
                    />
                    <img
                        ref={newImageRef}
                        className="evolution-pokemon-image evolution-new"
                        src={newPokemonImage}
                        alt="New Pokemon"
                        onError={(e) => {
                            e.target.src = '/images/pokemon/1.gif';
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default EvolutionAnimation;

